/**
 * ScaleInput — the 1..scaleMax rating row.
 *
 * MOBILE IS THE WHOLE POINT OF THIS COMPONENT. A 1–10 scale is ten touch
 * targets; at the iOS 44px minimum that needs ~440px plus gaps, against a 375px
 * viewport. It cannot be one row on a phone.
 *
 * So: `grid-cols-5` on mobile (two rows of five, ~75px per cell — comfortably
 * over 44px) and `sm:grid-cols-10` from 640px up (the single row in the design).
 * A horizontal scroll rail was the alternative and was rejected: for a rating
 * scale, options you cannot see are options you cannot weigh, and the whole
 * point is comparing 1 against 10.
 *
 * Scales below 6 stay on one row at every width — five-or-fewer cells already
 * fit — so `grid-cols-5` doubles as the small-scale layout for free.
 */
import { DEFAULT_HIGH_LABEL, DEFAULT_LOW_LABEL, DEFAULT_SCALE_MAX } from '@/api/surveys';

export interface ScaleInputProps {
  /** Upper bound; 1 is always the lower bound. */
  scaleMax?: number;
  value: number | null;
  onChange: (value: number) => void;
  lowLabel?: string;
  highLabel?: string;
  /** Renders non-interactive — used by the admin composer's live preview. */
  disabled?: boolean;
  /** Ties the group to its question label for screen readers. */
  ariaLabel?: string;
}

export function ScaleInput({
  scaleMax = DEFAULT_SCALE_MAX,
  value,
  onChange,
  lowLabel = DEFAULT_LOW_LABEL,
  highLabel = DEFAULT_HIGH_LABEL,
  disabled = false,
  ariaLabel,
}: ScaleInputProps) {
  const max = Math.min(Math.max(Math.round(scaleMax) || DEFAULT_SCALE_MAX, 2), 10);
  const options = Array.from({ length: max }, (_, i) => i + 1);
  // 6+ options need the two-row treatment on mobile; 5 or fewer already fit.
  const gridClass = max > 5 ? 'grid-cols-5 sm:grid-cols-10' : 'grid-cols-5';

  return (
    <div>
      <div role="radiogroup" aria-label={ariaLabel} className={`grid ${gridClass} gap-1.5`}>
        {options.map((n) => {
          const selected = value === n;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={String(n)}
              disabled={disabled}
              onClick={() => onChange(n)}
              className={`min-h-[44px] rounded-lg border text-sm font-medium transition-colors ${
                selected
                  ? 'border-primary bg-primary text-primary-foreground font-semibold'
                  : 'border-border bg-card text-foreground hover:bg-muted'
              } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
            >
              {n}
            </button>
          );
        })}
      </div>

      {/* Anchor labels. On mobile the row wraps, so these describe the ends of the
          SCALE rather than the ends of a visual line — hence plain
          justify-between under the grid rather than absolute positioning. */}
      <div className="mt-1.5 flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">{lowLabel}</span>
        <span className="text-[11px] text-muted-foreground text-right">{highLabel}</span>
      </div>
    </div>
  );
}

export default ScaleInput;
